import { ref, computed, getCurrentInstance } from '@vue/composition-api'
import i18n from '@/libs/i18n'
import store from '@/store'
import useEntityCrud from '@/composable/useEntityCrud'
import useEntityHelper from '@/composable/useEntityHelper'
import useErrorManager from '@/composable/useErrorManager'
import doctorMode from './components/doctor-mode'

function localizedKeywords(value, locale) {
  if (Array.isArray(value)) {
    return value.map(item => localizedKeywords(item, locale)).filter(Boolean).join(', ')
  }
  if (value && typeof value === 'object') {
    return typeof value[locale] === 'string' ? value[locale] : ''
  }
  return typeof value === 'string' ? value : ''
}

function localizedFile(value, locale) {
  if (!value) return ''
  if (typeof value === 'object') {
    if (value[locale]) return value[locale]
    // Older records stored one shared file instead of a locale-keyed object.
    if (value._id) return value
  }
  return ''
}

function localizedTags(value, locale) {
  if (!Array.isArray(value)) return null
  const localized = value.filter(item => item && item.language === locale)
  if (localized.length) return localized
  // Older records have no language on tags; retain those tags while editing.
  const legacy = value.filter(item => item && item._id && !item.language)
  return legacy.length ? legacy : null
}

export function localizedCampaignSettings(value, locale) {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return undefined
  const settings = value[locale]
  if (!settings || typeof settings !== 'object' || Array.isArray(settings)) return undefined
  if (!['automatic', 'manual', 'disabled'].includes(settings.mode)) return undefined
  if (!['inherit', 'automatic', 'end'].includes(settings.placementMode)) return undefined
  if (settings.mode === 'manual' && !/^[a-f0-9]{24}$/i.test(settings.campaignId || '')) return undefined

  const result = { mode: settings.mode, placementMode: settings.placementMode }
  if (settings.mode === 'manual') {
    result.campaignId = settings.campaignId
  }
  return result
}

export default function useArticlesList(type = 'article') {
  const router = getCurrentInstance().proxy.$router
  let ENDPOINT = ''
  if (router.history.current.name === 'pages-articles-list') {
    ENDPOINT = 'articles/search'
  } else if (router.history.current.name === 'pages-articles-add') {
    ENDPOINT = 'articles'
  } else if (router.history.current.name === 'pages-articles-edit') {
    ENDPOINT = 'articles'
  } else if (router.history.current.name === 'pages-article-categories-list') {
    ENDPOINT = 'articles-categories/search'
  } else if (router.history.current.name === 'pages-article-categories-add') {
    ENDPOINT = 'articles-categories'
  } else if (router.history.current.name === 'pages-article-categories-edit') {
    ENDPOINT = 'articles-categories'
  } else {
    ENDPOINT = 'articles'
  }
  const MODULE_NAME = `${type}s`
  const errorManager = useErrorManager()
  const { getAlt, createTagIdArray } = useEntityHelper()
  function convertResource(resource, selectedLang) {
    let watermark = resource.watermark || false
    if (resource.watermark && typeof resource.watermark === 'object') {
      watermark = resource.watermark[selectedLang] !== undefined ? resource.watermark[selectedLang] : false
    }
    return {
      ...resource,
      doctorBlockMode: doctorMode(resource.doctorBlocks && resource.doctorBlocks[selectedLang]),
      recommendedDoctorIds: resource.doctorBlocks && resource.doctorBlocks[selectedLang] ? resource.doctorBlocks[selectedLang].ids || [] : [],
      doctorBlockEnabled: Boolean(resource.doctorBlocks && resource.doctorBlocks[selectedLang] && resource.doctorBlocks[selectedLang].enabled === true),
      doctorBlockPlacement: resource.doctorBlocks && resource.doctorBlocks[selectedLang] ? resource.doctorBlocks[selectedLang].placement || 'middle' : 'middle',

      title: resource.title && resource.title[selectedLang] ? resource.title[selectedLang] : '',
      metaTitle: resource.metaTitle && resource.metaTitle[selectedLang] ? resource.metaTitle[selectedLang] : '',
      keywords: localizedKeywords(resource.keywords, selectedLang),
      description: resource.description && resource.description[selectedLang] ? resource.description[selectedLang] : '',
      content: resource.content && resource.content[selectedLang] ? resource.content[selectedLang] : null,
      lang: selectedLang,
      campaignSettings: localizedCampaignSettings(resource.campaignSettings, selectedLang),
      canonicalUrl: resource.canonicalUrl,
      articleThumbnail: localizedFile(resource.articleThumbnailFileId, selectedLang),
      articleCoverImage: localizedFile(resource.articleCoverImageFileId, selectedLang),
      articleVideo: localizedFile(resource.articleVideoFileId, selectedLang),
      approver: resource.approverId ? {
        _id: resource.approverId._id,
        slug: resource.approverId.slug ? resource.approverId.slug : '',
        firstName: resource.approverId.firstName ? resource.approverId.firstName[selectedLang] : '',
        lastName: resource.approverId.lastName ? resource.approverId.lastName[selectedLang] : '',
        managerImageFileId: resource.approverId.managerImageFileId,
      } : null,
      tags: localizedTags(resource.tagIds, selectedLang),
      writer: resource.writerId,
      editor: resource.editorId,
      relatedArticles: resource.relatedArticleIds && resource.relatedArticleIds.length
        ? resource.relatedArticleIds.map(itemEntry2 => ({
          _id: itemEntry2._id,
          title: itemEntry2.title ? itemEntry2.title[selectedLang] : '',
          articleThumbnailFileId: itemEntry2.articleThumbnailFileId,
        })) : [],
      relatedQuestions: resource.relatedQuestionIds && resource.relatedQuestionIds.length
        ? resource.relatedQuestionIds.map(itemEntry3 => ({
          _id: itemEntry3._id,
          title: itemEntry3.title ? itemEntry3.title[selectedLang] : '',
        })) : [],
      relatedServices: resource.relatedServiceIds && resource.relatedServiceIds.length
        ? resource.relatedServiceIds.map(itemEntry4 => ({
          _id: itemEntry4._id,
          title: itemEntry4.title ? itemEntry4.title[selectedLang] : '',
          serviceThumbnailFileId: itemEntry4.serviceThumbnailFileId,
        })) : [],
      relatedCategories: resource.relatedCategoryIds && resource.relatedCategoryIds.length
        ? resource.relatedCategoryIds.map(itemEntry5 => ({
          _id: itemEntry5._id,
          title: itemEntry5.title ? itemEntry5.title[selectedLang] : '',
        })) : [],
      category: resource.categoryId && resource.categoryId.title ? { _id: resource.categoryId._id, title: resource.categoryId.title[selectedLang] } : null,
      articleThumbnailAlt: getAlt('articleThumbnailFileId', resource, selectedLang),
      articleCoverImageAlt: getAlt('articleCoverImageFileId', resource, selectedLang),
      articleVideoAlt: getAlt('articleVideoFileId', resource, selectedLang),
      articleLink: { name: `pages-${type}s-edit`, params: { id: resource._id, lang: selectedLang } },
      embedVideo: resource.embedVideo ? resource.embedVideo[selectedLang] : null,
      watermark,
    }
  }

  const entity = useEntityCrud(MODULE_NAME, ENDPOINT, convertResource)
  const { itemLoading, item, form } = entity

  // Table Handlers
  const tableColumns = computed(() => [
    {
      key: 'title', label: i18n.t('general.title'), type: 'textImage', textKey: 'title', imageKey: 'articleThumbnail', linkKey: 'articleLink',
    },
    {
      key: 'category', label: i18n.t('general.category'), type: 'truncate-text', textSubKey: 'title', icon: 'GridIcon',
    },
    { key: 'status', label: i18n.t('articles.status'), icon: 'ClipboardIcon' },
    {
      key: 'approver', label: i18n.t('general.approver'), type: 'manager', icon: 'UserCheckIcon',
    },
    {
      key: 'writer', label: i18n.t('general.writer'), type: 'writer', icon: 'Edit2Icon', sortable: false,
    },
    {
      key: 'createdAt', label: i18n.t('general.createdAt'), type: 'dateTime', sortable: true,
    },
    {
      key: 'updatedAt', label: i18n.t('general.updatedAt'), type: 'dateTime', sortable: true,
    },
    { key: 'actions', label: i18n.t('general.actions'), sortable: false },
  ])

  const articleOptions = ref([])
  const articleOptions1 = ref([])
  const questionOptions = ref([])
  const serviceOptions = ref([])
  const categoryOptions = ref([])
  const categoryOptions1 = ref([])
  const rootCategoryOptions = ref([])
  const rootCategoryOptions1 = ref([])
  const statusOptions = ref(['Published', 'Draft'])
  const approverOptions = ref([])
  const isUniqueSlug = ref(true)

  const fetchServices = (lang = 'fa', search = '', searchBy = '') => {
    itemLoading.value = true
    store
      .dispatch('crud/fetchList', {
        queryParams: {
          lang, search, searchBy, limit: 40,
        },
        endpoint: 'services',
      })
      .then(response => {
        serviceOptions.value = response.data.data.map(itemEntry6 => ({ _id: itemEntry6._id, title: itemEntry6.title[lang], serviceThumbnailFileId: itemEntry6.serviceThumbnailFileId }))
      })
      .catch(err => {
        errorManager(err)
      })
      .finally(() => {
        const assignmentResult = false
        itemLoading.value = assignmentResult; return assignmentResult
      })
  }

  const fetchQuestions = (lang = 'fa', search = '', searchBy = '') => {
    itemLoading.value = true
    store
      .dispatch('crud/fetchList', {
        queryParams: {
          lang, search, searchBy, limit: 40,
        },
        endpoint: 'questions',
      })
      .then(response => {
        questionOptions.value = response.data.data.map(itemEntry7 => ({ _id: itemEntry7._id, title: itemEntry7.title[lang] }))
      })
      .catch(err => {
        errorManager(err)
      })
      .finally(() => {
        const assignmentResult = false
        itemLoading.value = assignmentResult; return assignmentResult
      })
  }
  const fetchArticlesList = (lang = '', search = '', searchBy = '', categoryId = '') => {
    itemLoading.value = true
    articleOptions1.value = []
    return store
      .dispatch('crud/fetchList', {
        queryParams: {
          lang, search, searchBy, ...(categoryId ? { categoryId } : {}), limit: 10,
        },
        endpoint: 'articles/search', // in baraye list va hamoon search
      })
      .then(response => {
        articleOptions1.value = response.data.data.map(itemEntry8 => ({ _id: itemEntry8._id, title: itemEntry8.title[lang], articleThumbnailFileId: itemEntry8.articleThumbnailFileId }))
      })
      .catch(err => {
        errorManager(err)
      })
      .finally(() => {
        const assignmentResult = false
        itemLoading.value = assignmentResult; return assignmentResult
      })
  }
  const fetchArticles = (lang = 'fa', search = '', searchBy = '') => {
    itemLoading.value = true
    articleOptions.value = []
    return store
      .dispatch('crud/fetchList', {
        queryParams: {
          lang, search, searchBy, type: 'article',
        },
        endpoint: 'articles/relatedArticles/', // in baraye option ha hast ke hamon api ghadimi bayad bashe
      })
      .then(response => {
        articleOptions.value = response.data.data.map(itemEntry9 => ({ _id: itemEntry9._id, title: itemEntry9.title[lang], articleThumbnailFileId: itemEntry9.articleThumbnailFileId }))
      })
      .catch(err => {
        errorManager(err)
      })
      .finally(() => {
        const assignmentResult = false
        itemLoading.value = assignmentResult; return assignmentResult
      })
  }
  const fetchCategoriesList = (lang = '', search = '', searchBy = '', parentId = '') => {
    itemLoading.value = true
    store
      .dispatch('crud/fetchList', {
        queryParams: {
          lang, search, searchBy, limit: 40, parentId,
        },
        endpoint: 'articles-categories',
      })
      .then(response => {
        if (parentId !== 'null') {
          categoryOptions1.value = response.data.data.map(itemEntry0 => ({ _id: itemEntry0._id, title: itemEntry0.title[lang] }))
        } else {
          rootCategoryOptions1.value = response.data.map(itemEntry1 => ({ _id: itemEntry1._id, title: itemEntry1.title[lang] }))
        }
      })
      .catch(err => {
        errorManager(err)
      })
      .finally(() => {
        const assignmentResult = false
        itemLoading.value = assignmentResult; return assignmentResult
      })
  }
  const fetchCategories = (lang = 'fa', search = '', searchBy = '', parentId = '') => {
    itemLoading.value = true
    store
      .dispatch('crud/fetchList', {
        queryParams: {
          lang, search, searchBy, parentId,
        },
        endpoint: 'articles-categories',
      })
      .then(response => {
        if (parentId !== 'null') {
          categoryOptions.value = response.data.data.map(itemEntry10 => ({ _id: itemEntry10._id, title: itemEntry10.title[lang] }))
        } else {
          rootCategoryOptions.value = response.data.map(itemEntry11 => ({ _id: itemEntry11._id, title: itemEntry11.title[lang] }))
        }
      })
      .catch(err => {
        errorManager(err)
      })
      .finally(() => {
        const assignmentResult = false
        itemLoading.value = assignmentResult; return assignmentResult
      })
  }

  const fetchApprovers = (lang = 'fa', search = '') => {
    itemLoading.value = true
    store
      .dispatch('crud/fetchList', {
        queryParams: { lang, search },
        endpoint: 'managers',
      })
      .then(response => {
        approverOptions.value = response.data.data.map(itemEntry12 => ({
          _id: itemEntry12._id,
          firstName: itemEntry12.firstName[lang],
          lastName: itemEntry12.lastName[lang],
          managerImageFileId: itemEntry12.managerImageFileId,
        }))
      })
      .catch(err => {
        errorManager(err)
      })
      .finally(() => {
        const assignmentResult = false
        itemLoading.value = assignmentResult; return assignmentResult
      })
  }

  const checkSlugIsUnique = (lang = 'fa', slug) => {
    if (slug === '' || (item.value && item.value.slug === form.value.slug)) {
      isUniqueSlug.value = true
      return
    }
    itemLoading.value = true
    store
      .dispatch('crud/fetchItem',
        {
          data: { id: slug },
          queryParams: { lang },
          endpoint: 'articles/slug',
        })
      .then(response => {
        isUniqueSlug.value = response.data.isUnique
      })
      .catch(err => {
        errorManager(err)
      })
      .finally(() => {
        const assignmentResult = false
        itemLoading.value = assignmentResult; return assignmentResult
      })
  }

  return {
    ...entity,
    approverOptions,
    articleOptions,
    categoryOptions,
    categoryOptions1,
    checkSlugIsUnique,
    convertResource,
    createTagIdArray,
    errorManager,
    fetchApprovers,
    fetchArticles,
    fetchArticlesList,
    fetchCategories,
    fetchCategoriesList,
    fetchQuestions,
    fetchServices,
    isUniqueSlug,
    questionOptions,
    rootCategoryOptions,
    rootCategoryOptions1,
    serviceOptions,
    statusOptions,
    tableColumns,
  }
}
